import React, { useContext, useMemo, FC } from 'react';
import { Action } from 'redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { ReactReduxContext } from 'react-redux';
import mergeInplace from 'lodash/merge';

import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import { ActionMap, ReducerActions } from 'ia-react-core/~/interfaces/ReducerActions';
import { ReducerSelectors, SelectorMap } from 'ia-react-core/~/interfaces/ReducerSelectors';
import { ReducerInfo } from 'ia-react-core/~/interfaces/ReducerInfo';
import { IdentifierMap, ReducerIdentifiers } from 'ia-react-core/~/interfaces/ReducerIdentifiers';

import { CoreAction } from 'ia-react-core/~/interfaces/CoreAction';
import { DeepPartial } from 'ia-react-core/~/interfaces/DeepPartial';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import throwError from '../../../utils/error/_throwError';
import ScopeContext from '../../../components/redux/_ScopeContext';
import registerVirtuals from '../../../utils/redux/_registerVirtuals';

let currentReducerId = 0;

export interface InjectReducerProps<State> {
  name?: string;
  scope?: string[];
  store?: AugmentedStore<State>;
}

type InitialStateFn<Props, State> = (props: Props, scope: string[]) => DeepPartial<State>;

export interface InjectReducerOptions<Props, State, CoreAction> {
  key: string | null | ((props: Props) => string);
  reducer: (state: State, action: CoreAction, props?: Props) => State;
  initialState?: DeepPartial<State> | null | InitialStateFn<Props, State>;
  useScope?: boolean;
  createScope?: boolean;
  cleanState?: boolean | ((props: Props) => boolean);
  namespace?: string;
  skip?: (props: Props) => boolean;
  selectors?: ReducerSelectors<Props>;
  identifiers?: ReducerIdentifiers<Props>;
  actions?: ReducerActions<Props>;
  idName?: string;
}

export interface InjectReducerMock {}

/**
 * @since 0.4.0
 * @param {string|function} key A key of the reducer
 * @param {function} reducer A reducer that will be injected
 * @see See [Redux](https://redux.js.org/)
 */
const injectReducer = <Props, State extends object = {}, A extends Action = CoreAction>({
  key, reducer, useScope = false, createScope = useScope, initialState, actions, selectors, cleanState = false,
  namespace, identifiers, skip, idName,
}: InjectReducerOptions<Props, State, A>) => {
  if (!key) {
    throwError('injectReducer: A "key" option is expected.');
  }

  return (WrappedComponent: FC<InjectReducerProps<State>>) => {
    interface ReducerInjectorProps {
      reducerId: number;
      name?: string;
      scope?: string[];
      newScope?: string[];
      store?: AugmentedStore<State, A>;
      wrappedReducer: (state: State, action: A) => State;
      reducerKey: string;
      reducerActions: ActionMap;
      reducerSelectors: SelectorMap;
      reducerIdentifiers: IdentifierMap;
      reducerInitialState: DeepPartial<State> | null;
      reducerCleanState: boolean;
    }

    class ReducerInjector extends React.PureComponent<ReducerInjectorProps> {
      static WrappedComponent = WrappedComponent;

      reducerInfo: Partial<ReducerInfo<State, Props, A>> = {};

      unregisterVirtuals: () => void;

      constructor(props: ReducerInjectorProps) {
        super(props);
        this.inject();
      }

      componentDidUpdate(prevProps: ReducerInjectorProps) {
        const { name, reducerKey, wrappedReducer } = this.props;

        const needsReloading = (
          prevProps.reducerKey !== reducerKey ||
          prevProps.name !== name
        );

        if (needsReloading) {
          this.eject(prevProps);
          this.inject();
        }

        this.refreshReducer(wrappedReducer);
      }

      componentWillUnmount() {
        this.eject(this.props);
      }

      inject() {
        const {
          reducerId, store, scope, wrappedReducer, reducerKey, reducerActions, reducerSelectors, reducerIdentifiers, reducerInitialState,
        } = this.props;
        const options = {
          scope,
          namespace,
          identifiers: reducerIdentifiers,
          initialState: reducerInitialState,
          idName,
        };

        if (!store.isReducerInjected(reducerKey, reducerId, wrappedReducer, options)) {
          const [virtuals, unregisterVirtuals] = registerVirtuals({
            actions: reducerActions,
            selectors: reducerSelectors,
            store,
            path: reducerKey,
            scope,
          });

          this.unregisterVirtuals = unregisterVirtuals;

          if (Object.keys(virtuals).length) {
            options.initialState = options.initialState || {} as DeepPartial<State>;
            options.initialState = mergeInplace(options.initialState, virtuals);
          }

          this.reducerInfo = store.injectReducer(reducerKey, reducerId, wrappedReducer, options);
        }
      }

      eject(props: ReducerInjectorProps) {
        const { reducerId, store, reducerKey, scope, reducerIdentifiers, reducerCleanState } = props;
        const options = { scope, namespace, identifiers: reducerIdentifiers, cleanState: reducerCleanState };

        store.ejectReducer(reducerKey, reducerId, options);
        this.unregisterVirtuals?.();
      }

      refreshReducer(updatedReducer: (state: State, action: A, props: Props) => State) {
        this.reducerInfo.reducer = updatedReducer;
      }

      render() {
        const {
          reducerId, scope, reducerKey, wrappedReducer, newScope, reducerActions, reducerSelectors, reducerIdentifiers,
          reducerInitialState, reducerCleanState, ...props
        } = this.props;
        return <WrappedComponent scope={newScope || scope} {...props} />;
      }
    }

    const InjectReducer = (props: Props & InjectReducerProps<State>) => {
      const shouldSkip = useMemo(() => skip && skip(props), [props]);

      const { store } = useContext(ReactReduxContext) as unknown as { store: AugmentedStore<State, A> };
      const { scope } = useContext(ScopeContext);

      const reducerId = useMemo(() => {
        currentReducerId += 1;
        return currentReducerId;
      }, []);

      const config = useMemo(() => !shouldSkip && ({
        wrappedReducer: (state: State, action: A) => reducer(state, action, props),
        reducerKey: (typeof key === 'function') ? key(props) : key,
        reducerActions: (typeof actions === 'function') ? actions(props) : actions,
        reducerSelectors: (typeof selectors === 'function') ? selectors(props) : selectors,
        reducerIdentifiers: (typeof identifiers === 'function') ? identifiers(props) : identifiers,
        reducerInitialState: (typeof initialState === 'function' ? (initialState as InitialStateFn<Props, State>)(props, scope) : initialState),
        reducerCleanState: (typeof cleanState === 'function' ? cleanState(props) : cleanState),
      }), [props, scope, shouldSkip]);

      const newScope = useMemo(() => (
        createScope ? [...scope, ...config.reducerKey.split('.')] : scope
      ), [config.reducerKey, scope]);

      const newScopeContext = useMemo(() => ({ scope: newScope, store }), [newScope, store]);

      if (shouldSkip) {
        return <WrappedComponent {...props} />;
      }

      if (useScope) {
        return (
          <ScopeContext.Provider value={newScopeContext}>
            <ReducerInjector
              {...props}
              store={store}
              scope={scope}
              newScope={newScope}
              reducerId={reducerId}
              {...config}
            />
          </ScopeContext.Provider>
        );
      }

      return (
        <ReducerInjector
          {...props}
          store={store}
          reducerId={reducerId}
          {...config}
        />
      );
    };

    InjectReducer.displayName = getWrappedDisplayName('InjectReducer', WrappedComponent);

    return hoistNonReactStatics(InjectReducer, WrappedComponent);
  };
};

/** @component */
export default injectReducer;
